import { useMount } from 'react-use';

import {
   VisitLocationNotUpdatableFault,
   VisitLocationUpdateCommand,
   VisitQuery,
} from '@dreryk/egabinet-visit-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { assertUnreachable } from '@dreryk/react-common';
import { dispatchSnackbarInformativeEvent, dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { visitTranslationKeys } from '../../translations';

import { VisitLocationData, VisitLocationType } from './VisitLocation.types';

type VisitLocationUpdateCommandParameters = {
   visitLocation: VisitLocationData;
};

type UseVisitLocationUpdateCommandOptions = {
   visitId: number;
   onSuccess: (visitLocation: VisitLocationData) => void;
};

export const useVisitLocationUpdateCommand = (
   options: UseVisitLocationUpdateCommandOptions
): UseCqrsCommandResult<void, VisitLocationUpdateCommandParameters> => {
   return useCqrsCommand<void, VisitLocationUpdateCommandParameters, VisitLocationNotUpdatableFault>(
      (parameters) => {
         const command = new VisitLocationUpdateCommand();
         const visitLocationType = parameters.visitLocation.type;
         command.visitId = options.visitId;

         switch (visitLocationType) {
            case VisitLocationType.FACILITY_LOCATION:
               command.healthcareFacilityLocationId = Slot.of(parameters.visitLocation.id);
               return command;
            case VisitLocationType.FACILITY_UNIT:
               command.healthcareFacilityUnitId = Slot.of(parameters.visitLocation.id);
               return command;
            case VisitLocationType.SERVICE_PROVIDING_LOCATION:
               command.serviceProvidingLocationId = Slot.of(parameters.visitLocation.id);
               return command;
            default:
               return assertUnreachable(visitLocationType);
         }
      },
      {
         onSuccess: (__, parameters) => {
            options.onSuccess(parameters.visitLocation);
         },
         onFault: {
            'egabinet-visit-frontend-api:VisitLocationNotUpdatableFault': () => {
               dispatchSnackbarNegativeEvent(visitTranslationKeys.visitLocationNotUpdatableFault);
               refetchCqrsQuery(VisitQuery);
            },
         },
      }
   );
};

type FirstVisitLocationSelectorOptions = {
   chosenVisitLocationId: number | undefined;
   visitLocations: VisitLocationData[];
   onVisitLocationChange: (visitLocation: VisitLocationData) => void;
};

export const useFirstVisitLocationSelector = (options: FirstVisitLocationSelectorOptions): void => {
   useMount(() => {
      if (!options.chosenVisitLocationId) {
         const [firstServiceProvidingLocation] = options.visitLocations;
         options.onVisitLocationChange(firstServiceProvidingLocation);
         dispatchSnackbarInformativeEvent(
            visitTranslationKeys.previousServiceProvidingLocationNotAvailableNewHasBeenChosen
         );
      }
   });
};
